import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";
import { LayoutDashboard, CalendarDays, Wallet, BarChart3, Home, LogOut, Building2, ClipboardList, MessageSquare } from "lucide-react";

export default function AppShell({ children }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/app", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/properties", label: t("properties_page"), icon: Building2, testid: "nav-properties" },
    { to: "/app/calendar", label: t("calendar"), icon: CalendarDays, testid: "nav-calendar" },
    { to: "/app/bookings", label: t("bookings_history"), icon: ClipboardList, testid: "nav-bookings" },
    { to: "/app/finances", label: t("finances"), icon: Wallet, testid: "nav-finances" },
    { to: "/app/reports", label: t("reports"), icon: BarChart3, testid: "nav-reports" },
    { to: "/app/feedback", label: t("feedback"), icon: MessageSquare, testid: "nav-feedback" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen grain-bg">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <NavLink to="/app" className="flex items-center gap-2 font-semibold text-[#2A5948]">
              <Home size={20} />
              <span className="text-lg">{t("brand")}</span>
            </NavLink>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <LanguageSwitcher />
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="admin-link"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#2A5948] text-white text-sm hover:bg-[#1F4235]"
              >
                {t("admin")}
              </NavLink>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E1D8] bg-white/70 text-sm text-[#5C6053] hover:bg-white transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t("logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <nav className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-[#E5E1D8] p-2 shadow-sm">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/app"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-1 transition-colors ${
                    isActive
                      ? "bg-[#2A5948] text-white"
                      : "text-[#1C1E17] hover:bg-[#F3EFE7]"
                  }`
                }
              >
                <it.icon size={18} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <main className="min-w-0">
          {/* Mobile tabs */}
          <div className="lg:hidden mb-4 flex gap-2 overflow-x-auto pb-2">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/app"}
                data-testid={`${it.testid}-mobile`}
                className={({ isActive }) =>
                  `whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-full text-sm border ${
                    isActive
                      ? "bg-[#2A5948] text-white border-[#2A5948]"
                      : "bg-white text-[#1C1E17] border-[#E5E1D8]"
                  }`
                }
              >
                <it.icon size={14} />
                {it.label}
              </NavLink>
            ))}
          </div>

          {children}
        </main>
      </div>

      <footer className="max-w-7xl mx-auto px-4 py-8 text-sm text-[#5C6053]">
        <span>© {new Date().getFullYear()} {t("brand")}</span>
      </footer>
    </div>
  );
}
